import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCurrentUser, getAdminById, updateAdmin, updateAdminPassword, updateCurrentUser, updateCurrentAvatar } from '../../api/auth';
import { message } from 'antd';

// 获取当前用户信息
export const fetchCurrentUser = createAsyncThunk(
  'profile/fetchCurrentUser',
  async () => {
    const response = await getCurrentUser();
    return response.data;
  }
);

// 获取管理员详情
export const fetchAdminById = createAsyncThunk(
  'profile/fetchAdminById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getAdminById(id);
      if (response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response.message || '获取管理员信息失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取管理员信息失败');
    }
  }
);

// 更新管理员信息
export const updateProfile = createAsyncThunk(
  'profile/updateProfile',
  async ({ id, adminUserDTO }, { rejectWithValue }) => {
    try {
      const response = await updateAdmin(id, adminUserDTO);
      if (response.code === 200) {
        message.success('个人信息更新成功');
        // 重新获取管理员信息
        const userResponse = await getAdminById(id);
        if (userResponse.code === 200) {
          return userResponse.data;
        }
        return rejectWithValue('更新成功，但获取最新信息失败');
      }
      return rejectWithValue(response.message || '更新失败');
    } catch (error) {
      return rejectWithValue(error.message || '更新失败');
    }
  }
);

// 更新当前管理员个人信息
export const updateCurrentUserProfile = createAsyncThunk(
  'profile/updateCurrentUserProfile',
  async (updateDTO, { rejectWithValue, dispatch }) => {
    try {
      // 调用updateCurrentUser API更新当前管理员个人信息
      const response = await updateCurrentUser(updateDTO);
      if (response.code === 200) {
        message.success('个人信息更新成功');
        // 重新获取当前用户信息以同步最新数据
        const userResponse = await dispatch(fetchCurrentUser());
        return userResponse.payload;
      }
      return rejectWithValue(response.message || '更新失败');
    } catch (error) {
      return rejectWithValue(error.message || '更新失败');
    }
  }
);

// 更新当前管理员头像
export const updateCurrentAvatarProfile = createAsyncThunk(
  'profile/updateCurrentAvatarProfile',
  async (avatarUrl, { rejectWithValue, dispatch }) => {
    try {
      // 调用updateCurrentAvatar API更新当前管理员头像
      const response = await updateCurrentAvatar({ avatarUrl });
      if (response.code === 200) {
        message.success('头像更新成功');
        // 重新获取当前用户信息以同步最新数据
        const userResponse = await dispatch(fetchCurrentUser());
        return userResponse.payload;
      }
      return rejectWithValue(response.message || '头像更新失败');
    } catch (error) {
      return rejectWithValue(error.message || '头像更新失败');
    }
  }
);

// 修改密码
export const changeUserPassword = createAsyncThunk(
  'profile/changePassword',
  async ({ id, oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await updateAdminPassword(id, oldPassword, newPassword);
      if (response.code === 200) {
        message.success('密码修改成功');
        return true;
      }
      return rejectWithValue(response.message || '密码修改失败');
    } catch (error) {
      return rejectWithValue(error.message || '密码修改失败');
    }
  }
);

const initialState = {
  currentUser: null,
  adminDetail: null,
  loading: false,
  passwordLoading: false,
  error: null,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取用户信息
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // 获取管理员详情
      .addCase(fetchAdminById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminById.fulfilled, (state, action) => {
        state.loading = false;
        state.adminDetail = action.payload;
      })
      .addCase(fetchAdminById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // 更新用户信息
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // 更新当前管理员个人信息
      .addCase(updateCurrentUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCurrentUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        // 更新成功后，同步更新currentUser
        if (action.payload) {
          state.currentUser = action.payload;
        }
      })
      .addCase(updateCurrentUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // 更新当前管理员头像
      .addCase(updateCurrentAvatarProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCurrentAvatarProfile.fulfilled, (state, action) => {
        state.loading = false;
        // 更新成功后，同步更新currentUser
        if (action.payload) {
          state.currentUser = action.payload;
        }
      })
      .addCase(updateCurrentAvatarProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // 修改密码
      .addCase(changeUserPassword.pending, (state) => {
        state.passwordLoading = true;
        state.error = null;
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.passwordLoading = false;
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.passwordLoading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearError } = profileSlice.actions;
export default profileSlice.reducer; 